import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api } from '../api';

/** Copy of the events module's starting systems (migration 0003), stable ids. */
export const SYSTEM_IDS = {
  own: '01990000-0000-7000-8000-00000000e501',
  client: '01990000-0000-7000-8000-00000000e502',
  venue: '01990000-0000-7000-8000-00000000e503',
  mixed: '01990000-0000-7000-8000-00000000e504',
  none: '01990000-0000-7000-8000-00000000e505',
} as const;

export const SYSTEMS_RESOURCE = 'events.systems';
export const SYSTEMS_KEY = ['events', 'systems'] as const;

export type SystemKind = 'own' | 'external' | 'none';
export const SYSTEM_KIND_LABELS: Record<SystemKind, string> = { own: 'Nasz', external: 'Z zewnątrz', none: 'Bez systemu' };

/** As `GET /api/events/systems` sends it. */
export type SystemItem = {
  id: string;
  name: string;
  kind: SystemKind;
  contractorId: string | null;
  contractorName: string | null;
  blockId: string | null;
  blockName: string | null;
  description: string;
  deleted: boolean;
  version: number;
};

export function useSystems(enabled = true) {
  return useQuery({ queryKey: SYSTEMS_KEY, enabled, retry: false, queryFn: () => api<{ systems: SystemItem[] }>('/api/events/systems') });
}

/** The system a new event starts with: "Nasz system" while it is on the list, else the first live one of ours, else none. */
export function defaultSystemId(list: readonly SystemItem[]): string {
  const live = list.filter((s) => !s.deleted && s.kind === 'own');
  return (live.find((s) => s.id === SYSTEM_IDS.own) ?? live[0])?.id ?? '';
}

/**
 * Fills the system of a new event once the list has loaded (owner remarks 3/13, 3/15: a typical event goes on
 * with one "Dalej"), unless something is chosen already or the role may not write the field.
 */
export function useDefaultSystem(current: string, set: (id: string) => void, enabled: boolean): void {
  const list = useSystems(enabled).data?.systems;
  const done = useRef(false);
  useEffect(() => {
    if (!enabled || done.current || !list) return;
    done.current = true;
    const id = defaultSystemId(list);
    if (current === '' && id !== '') set(id);
  }, [enabled, list, current, set]);
}

/** The choice of a system: ours, from outside, then "Bez systemu"; a deleted one only when it is `current`. */
export function systemGroups(list: readonly SystemItem[], current: string | null | ''): Array<{ label: string; items: SystemItem[] }> {
  const shown = list.filter((s) => !s.deleted || s.id === current);
  return [
    { label: 'Nasze', items: shown.filter((s) => s.kind === 'own') },
    { label: 'Z zewnątrz', items: shown.filter((s) => s.kind === 'external') },
    { label: '', items: shown.filter((s) => s.kind === 'none') },
  ].filter((g) => g.items.length > 0);
}

/** "L-Acoustics L2 — Szmyt (z zewnątrz: Szmyt)", "Stary system (usunięty)"; '' for none chosen. */
export function systemLabel(list: readonly SystemItem[], id: string | null | undefined): string {
  if (!id) return '';
  const s = list.find((x) => x.id === id);
  if (!s) return '';
  const notes = [s.kind === 'external' ? `z zewnątrz${s.contractorName ? `: ${s.contractorName}` : ''}` : '', s.deleted ? 'usunięty' : ''].filter(Boolean);
  return notes.length > 0 ? `${s.name} (${notes.join(', ')})` : s.name;
}
